import { DefaultPrimaryButton, DefaultSecondaryButton } from '@frontend/components/common';
import { DefaultModal, ModalType } from '@frontend/components/kiosk';
import { CommonMessage } from '@frontend/lang';
import { TransactionTemplateClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionTrigger, TransactionTriggerName, TransactionTriggerTemplate } from '@frontend/transaction/types';
import { type ReactNode, useEffect, useState } from 'react';
import { LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { useAppSelector } from '../../redux/store';
import { SlotTransaction } from './slot-detail.controller';

export function getCustomTransactionTriggerMessage(trigger: TransactionTriggerName): ReactNode {
    switch (trigger) {
        case TransactionTriggerName.TRANSACTION_PICK_UP:
            return CommonMessage.OBJECTS.TRANSACTION.PICKED_UP;
        case TransactionTriggerName.TRANSACTION_DROP_OFF:
            return CommonMessage.OBJECTS.TRANSACTION.DROPPED_OFF;
        case TransactionTriggerName.TRANSACTION_CANCEL:
            return CommonMessage.OBJECTS.TRANSACTION.CANCELLED;
        case TransactionTriggerName.TRANSACTION_COMPLETE:
            return CommonMessage.OBJECTS.TRANSACTION.COMPLETED;
        default:
            return TransactionTrigger.getByValue(trigger)?.displayName ?? trigger;
    }
}

export interface UpdateTransactionStatusModalProps {
    transaction: SlotTransaction | null;
    show: boolean;
    onClose: () => void;
    /** Called when trigger is sent successfully. Passes the trigger so parent can show optimistic UI. */
    onSuccess: (trigger?: TransactionTriggerName) => void;
}

export const UpdateTransactionStatusModal = (props: UpdateTransactionStatusModalProps) => {
    const navigationState = useAppSelector((store) => store.navigation);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState<ReactNode | null>(null);
    const [selectedTrigger, setSelectedTrigger] = useState<TransactionTriggerName | null>(null);
    const [triggerTemplate, setTriggerTemplate] = useState<TransactionTriggerTemplate | undefined>(undefined);
    const [allowedTriggers, setAllowedTriggers] = useState<TransactionTriggerName[]>([]);

    useEffect(() => {
        if (props.show) {
            setSelectedTrigger(null);
            setError(null);
            TransactionTemplateClient.fetchTransactionStateTriggerTemplate().then(setTriggerTemplate);
        }
    }, [props.show, props.transaction?.id]);

    useEffect(() => {
        if (!triggerTemplate || !props.transaction) return;
        const triggers = triggerTemplate[props.transaction.state];
        setAllowedTriggers(triggers ?? []);
    }, [triggerTemplate, props.transaction?.state]);

    if (!props.show) return null;
    const transaction = props.transaction;
    if (!transaction) return null;

    const handleClose = () => {
        setSelectedTrigger(null);
        setError(null);
        props.onClose();
    };

    const handleUpdate = () => {
        if (selectedTrigger == null) return;
        setError(null);
        setUpdating(true);
        TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
            trigger: selectedTrigger,
            location: navigationState.spot?.id ?? undefined,
            actor: navigationState.user?.id ?? undefined
        })
            .then(() => {
                props.onSuccess(selectedTrigger);
                handleClose();
            })
            .catch((e) => {
                setError(e?.message ?? CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => setUpdating(false));
    };

    return (
        <DefaultModal
            show={props.show}
            handleClose={handleClose}
            type={ModalType.PROMPT}
            className='m-4 flex w-[90vw] flex-col overflow-hidden'>
            <div className='bg-background-surface flex min-h-0 flex-1 flex-col items-center justify-center gap-5 rounded-3xl p-6'>
                <div className='text-foreground-default flex min-h-0 w-full flex-1 flex-col items-center justify-start gap-6'>
                    <div className='flex w-full flex-row justify-between'>
                        <span className='text-xl font-semibold'>
                            <FormattedMessage
                                id='kiosk.manage-spot.slot-detail.update-transaction-status.title'
                                defaultMessage='Update transaction status'
                            />
                        </span>
                        <LuX
                            className='size-6 cursor-pointer'
                            onClick={handleClose}
                            aria-label='Close'
                        />
                    </div>
                    {error && (
                        <div
                            className='text-status-danger w-full text-sm'
                            role='alert'>
                            {error}
                        </div>
                    )}
                    <div className='flex min-h-0 w-full flex-1 flex-col gap-2 overflow-y-auto'>
                        {allowedTriggers.length === 0 && !triggerTemplate && (
                            <span className='text-brand-muted'>{CommonMessage.FORMS.LOADING_PLACEHOLDER}</span>
                        )}
                        {allowedTriggers.length === 0 && triggerTemplate && (
                            <span className='text-brand-muted'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.slot-detail.update-transaction-status.no-transitions'
                                    defaultMessage='No status changes available for this transaction.'
                                />
                            </span>
                        )}
                        {allowedTriggers.map((triggerName) => {
                            const isSelected = selectedTrigger === triggerName;
                            return (
                                <button
                                    key={triggerName}
                                    type='button'
                                    disabled={updating}
                                    onClick={() => setSelectedTrigger((prev) => (prev === triggerName ? null : triggerName))}
                                    className={`flex w-full items-center justify-between rounded-lg px-4 py-3 outline-1 -outline-offset-1 disabled:opacity-50 ${
                                        isSelected
                                            ? 'bg-brand-light outline-brand-secondary text-brand-secondary outline'
                                            : 'bg-background-surface outline-border-default text-foreground-default outline'
                                    }`}>
                                    <span className='text-base font-medium'>{getCustomTransactionTriggerMessage(triggerName)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className='flex w-full items-center justify-end gap-2'>
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.CLOSE}
                        onClick={handleClose}
                        disabled={updating}
                    />
                    <DefaultPrimaryButton
                        text={
                            <FormattedMessage
                                id='kiosk.manage-spot.slot-detail.update-status-button'
                                defaultMessage='Update'
                            />
                        }
                        onClick={handleUpdate}
                        disabled={updating || selectedTrigger == null}
                    />
                </div>
            </div>
        </DefaultModal>
    );
};
